import { useMemo, useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import {
  AGENT_CONNECTIONS, CREATIVE_APIS, STORAGE_PUBLISHING, MARKETING_CRM,
  STATE_LABEL, STATE_TONE, MCP_URL, INSTALL_PROMPT,
} from "../lib/sample-data";
import ApiDashboard from "../components/integrations/ApiDashboard";
import {
  Plug, Search, Copy, Check, ExternalLink, Bot, Sparkles, Terminal, Webhook,
  Cloud, Package, Building2,
} from "lucide-react";
import { cn } from "../lib/utils";

const SECTIONS = [
  { id: "agent", label: "Agent AI Connections", icon: Bot },
  { id: "creative", label: "Creative AI APIs", icon: Sparkles },
  { id: "storage", label: "Storage & Publishing", icon: Cloud },
  { id: "marketing", label: "Marketing & CRM", icon: Building2 },
  { id: "api", label: "API Dashboard", icon: Package },
];

const CONNECTOR_ICON = { agent: Bot, mcp: Plug, cli: Terminal, webhook: Webhook };

export default function Integrations() {
  const [section, setSection] = useState("agent");
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(null);

  const copy = (k, v) => {
    navigator.clipboard?.writeText(v);
    setCopied(k);
    setTimeout(() => setCopied(null), 1400);
  };

  const counts = useMemo(() => {
    const all = [...AGENT_CONNECTIONS, ...CREATIVE_APIS, ...STORAGE_PUBLISHING, ...MARKETING_CRM];
    const map = {};
    all.forEach((i) => { map[i.state] = (map[i.state] || 0) + 1; });
    return map;
  }, []);

  return (
    <div data-testid="integrations-page" className="space-y-8">
      <PageHeader
        kicker="System · Integrations"
        title="Connect your tools, agents, and APIs"
        description="Wire AI agents, creative APIs, storage, publishing, and marketing tools. Workspace-owned keys only — platform secrets stay in the platform."
      />

      {/* State summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(STATE_LABEL).map(([k, label]) => (
          <div
            key={k}
            data-testid={`int-state-${k}`}
            className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60"
          >
            <div className="flex items-center gap-2">
              <span className={cn("h-2.5 w-2.5 rounded-full", STATE_TONE[k].dot)} />
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">{label}</div>
            </div>
            <div className="font-display text-2xl font-semibold mt-1.5 text-[var(--pl-text)]">
              {counts[k] || 0}
            </div>
          </div>
        ))}
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="int-section-tabs">
        {SECTIONS.map((s) => {
          const I = s.icon;
          return (
            <button
              key={s.id}
              data-testid={`int-tab-${s.id}`}
              onClick={() => setSection(s.id)}
              className={cn(
                "px-4 h-10 rounded-xl text-xs inline-flex items-center gap-2 border transition",
                section === s.id
                  ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                  : "border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
              )}
            >
              <I className="h-3.5 w-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      {section !== "api" && (
        <div className="flex items-center gap-2 px-3 h-11 rounded-xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
          <Search className="h-4 w-4 text-[var(--pl-text-muted)]" />
          <input
            data-testid="int-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search integrations…"
            className="bg-transparent flex-1 text-sm outline-none text-[var(--pl-text)] placeholder:text-[var(--pl-text-muted)]"
          />
        </div>
      )}

      {/* Section: Agent AI Connections */}
      {section === "agent" && (
        <div data-testid="int-agent-section" className="space-y-6">
          {/* Higgsfield-style connect card */}
          <div
            data-testid="int-higgsfield-connect"
            className="rounded-3xl border border-[var(--pl-border)] p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(39,230,209,0.10), rgba(185,255,47,0.06), rgba(11,47,44,0.6))",
            }}
          >
            <div className="lg:col-span-3 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Connect PremierLab to your AI agents
              </div>
              <h2 className="font-display text-2xl font-medium text-[var(--pl-text)] tracking-tight">
                Generate from your conversations
              </h2>
              <p className="text-sm text-[var(--pl-text)]/80 max-w-xl">
                Connect PremierLab to Claude, OpenClaw, Hermes, NemoClaw, Cursor, Codex, and other AI agents so you can generate cinematic images, videos, books, slides, ads, and campaigns directly from your conversations.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  data-testid="int-copy-mcp"
                  onClick={() => copy("mcp", MCP_URL)}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 h-10 rounded-xl text-xs transition",
                    copied === "mcp"
                      ? "bg-[var(--pl-lime)] text-[#061414]"
                      : "border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
                  )}
                >
                  {copied === "mcp" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === "mcp" ? "Copied" : "Copy MCP URL"}
                </button>
                <button
                  data-testid="int-copy-install"
                  onClick={() => copy("install", INSTALL_PROMPT)}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 h-10 rounded-xl text-xs transition",
                    copied === "install"
                      ? "bg-[var(--pl-lime)] text-[#061414]"
                      : "border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
                  )}
                >
                  {copied === "install" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === "install" ? "Copied" : "Copy Install Prompt"}
                </button>
                <a
                  href="/agent-ai"
                  data-testid="int-open-agent-ai"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl pl-btn-aqua text-xs"
                >
                  <Plug className="h-3.5 w-3.5" /> Open Agent AI
                </a>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-3">
              <div className="rounded-2xl border border-[var(--pl-border)] bg-black/30 p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-1.5">MCP URL</div>
                <code className="block font-mono text-xs text-[var(--pl-text)] break-all">{MCP_URL}</code>
              </div>
              <ol className="text-xs text-[var(--pl-text-muted)] space-y-1 list-decimal pl-5">
                <li>Open your AI agent settings</li>
                <li>Add a custom connector</li>
                <li>Paste the MCP URL above</li>
                <li>Connect and sign in</li>
                <li>Ask your agent to create</li>
              </ol>
            </div>
          </div>

          <ConnectorGrid
            items={filterByQ(AGENT_CONNECTIONS, q)}
            iconResolver={(it) => CONNECTOR_ICON[it.connector] || Bot}
            extraButton={(it) => ({
              testId: `int-copy-prompt-${it.id}`,
              label: copied === `prompt-${it.id}` ? "Copied" : "Setup prompt",
              icon: copied === `prompt-${it.id}` ? Check : Copy,
              onClick: () => copy(`prompt-${it.id}`, INSTALL_PROMPT),
            })}
          />
        </div>
      )}

      {/* Section: Creative AI APIs */}
      {section === "creative" && (
        <div data-testid="int-creative-section" className="space-y-3">
          <div className="rounded-xl border border-[var(--pl-aqua)]/30 bg-[var(--pl-aqua)]/5 p-4 text-sm text-[var(--pl-text-muted)]">
            <span className="text-[var(--pl-aqua)]">Workspace-owned keys only.</span> PremierLab platform keys
            are never exposed to the user portal. Connect your own ElevenLabs, Higgsfield, HeyGen, and others below.
          </div>
          <ConnectorGrid items={filterByQ(CREATIVE_APIS, q)} showCategory />
        </div>
      )}

      {/* Section: Storage & Publishing */}
      {section === "storage" && (
        <div data-testid="int-storage-section">
          <ConnectorGrid items={filterByQ(STORAGE_PUBLISHING, q)} />
        </div>
      )}

      {/* Section: Marketing & CRM */}
      {section === "marketing" && (
        <div data-testid="int-marketing-section">
          <ConnectorGrid items={filterByQ(MARKETING_CRM, q)} />
        </div>
      )}

      {/* Section: API Dashboard */}
      {section === "api" && (
        <div data-testid="int-api-section">
          <ApiDashboard scope="integrations" />
        </div>
      )}
    </div>
  );
}

function filterByQ(arr, q) {
  if (!q) return arr;
  const v = q.toLowerCase();
  return arr.filter((i) => i.name.toLowerCase().includes(v) || (i.desc || "").toLowerCase().includes(v));
}

function ConnectorGrid({ items, iconResolver, extraButton, showCategory }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((i, idx) => {
        const tone = STATE_TONE[i.state];
        const I = iconResolver ? iconResolver(i) : Plug;
        const extra = extraButton?.(i);
        return (
          <div
            key={i.id}
            data-testid={`integration-${i.id}`}
            className={cn(
              "pl-fade-up pl-contour-card pl-lift rounded-2xl p-5 border bg-[var(--pl-surface)]/60 backdrop-blur-md",
              tone.border
            )}
            style={{ animationDelay: `${idx * 25}ms` }}
          >
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center">
                <I className="h-5 w-5 text-[var(--pl-aqua)]" />
              </div>
              <span className={cn("inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border", tone.border, tone.text)}>
                <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
                {STATE_LABEL[i.state]}
              </span>
            </div>
            <div className="mt-3">
              <div className="font-medium text-[var(--pl-text)]">{i.name}</div>
              {showCategory && i.category && (
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)] mt-0.5">
                  {i.category}
                </div>
              )}
              <div className="text-xs text-[var(--pl-text-muted)] mt-2">{i.desc}</div>
            </div>
            <div className="mt-4 flex items-center flex-wrap gap-2">
              <button
                data-testid={`int-connect-${i.id}`}
                className={cn(
                  "h-9 px-3 rounded-xl text-xs",
                  i.state === "connected"
                    ? "border border-[var(--pl-border)] text-[var(--pl-text)]"
                    : "pl-btn-aqua"
                )}
              >
                {i.state === "connected" ? "Manage" : "Connect"}
              </button>
              <button
                data-testid={`int-test-${i.id}`}
                className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:border-[var(--pl-aqua)]/40"
              >
                Test
              </button>
              {extra && (
                <button
                  data-testid={extra.testId}
                  onClick={extra.onClick}
                  className="h-9 px-3 rounded-xl border border-[var(--pl-border)] text-xs text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] inline-flex items-center gap-1.5"
                >
                  <extra.icon className="h-3.5 w-3.5" />
                  {extra.label}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
